import { type ActionImpl, useKBar } from 'kbar';
import { observer } from 'mobx-react-lite';

import { XStack } from '@/components/ui';

import { hasSubmenu } from './preview-model';

import type { CommandBarActionImpl } from './actions/types';

type Props = {
  results: (string | ActionImpl)[];
  showScope: boolean;
};

function getPrimaryHint(item: string | ActionImpl | undefined) {
  if (!item || typeof item === 'string') return null;
  const action = item as CommandBarActionImpl;
  if (action.preview?.kind === 'message') {
    return { key: '↵', label: 'Open at message' };
  }
  if (action.preview?.kind === 'see_all') {
    return { key: '↵', label: 'Show all' };
  }
  if (hasSubmenu(action)) {
    return { key: '→', label: 'Choose' };
  }
  if (action.isSearchResult) return { key: '↵', label: 'Open' };
  return { key: '↵', label: 'Run' };
}

function Hint({ keyLabel, label }: { keyLabel: string; label: string }) {
  return (
    <XStack className="items-center gap-1.5">
      <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-border-strong bg-surface-hover px-1 text-[11px] text-foreground-muted">
        {keyLabel}
      </kbd>
      {label}
    </XStack>
  );
}

export const CommandBarFooter = observer(function CommandBarFooter({
  results,
  showScope,
}: Props) {
  const { activeIndex } = useKBar((state) => ({
    activeIndex: state.activeIndex,
  }));
  const primary = getPrimaryHint(results[activeIndex]);

  return (
    <XStack className="h-9 items-center gap-4 border-t border-border-strong px-4 text-xs text-foreground-muted">
      <Hint keyLabel="↑↓" label="Navigate" />
      {primary && <Hint keyLabel={primary.key} label={primary.label} />}
      {showScope && <Hint keyLabel="tab" label="Scope" />}
      <XStack className="ml-auto items-center">
        <Hint keyLabel="esc" label="Close" />
      </XStack>
    </XStack>
  );
});
